import { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { differenceInDays, startOfToday } from 'date-fns';
import { useFormState } from 'react-final-form';
import newTZDate from 'src/common/utils/dateHelpers';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import DatePickerAndModal from 'src/book-now-direct/components/DatePickerAndModal';
import DurationField from 'src/book-now-direct/components/DurationField';
import DepartureTimeField from 'src/book-now-direct/components/DepartureTimeField';
import GuestField from 'src/book-now-direct/components/GuestField';
import Divider from 'src/common/components/dockyard/Divider';
import CaptainOption from 'src/book-now-direct/components/CaptainOption';
import PricingPanel from 'src/book-now-direct/components/PricingPanel';
import Img from 'src/common/components/Img';
import Typography from 'src/common/components/Typography';
import type { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import OccasionField from 'src/common/components/OccasionField';
import { MESSAGE_TO_OWNER, OCCASION } from 'src/book-now-direct/constants';
import TextArea from 'src/common/components/TextArea';
import type { Occasion } from 'src/common/types/api/v4/trips/occasions';
import {
  BOOKING_MOTIVATOR_STATES, PATHS, SUPPORT_URLS,
} from 'src/common/constants';
import BookingMotivator from 'src/common/components/BookingMotivator';
import UniversalLink from 'src/common/components/UniversalLink';
import styles from './FormStepDetails.module.scss';

export type Props = {
  listing: FullBoatSearchDetail;
  occasions: Occasion[];
};

const FormStepDetails: FC<Props> = ({
  listing,
  occasions,
}) => {
  const [tDirect] = useTranslation('book-now-direct');
  const [tCommon] = useTranslation('common');
  const { values } = useFormState();
  const { departDate } = values;

  const {
    formStep,
    previewInstabooksOnDate,
    blockedTimesLoading,
    blockedTimes,
  } = useBookNowDirectContext();

  const {
    getSelectedMotivators,
  } = useBookingMotivatorsContext();

  const previewDate = previewInstabooksOnDate[0]?.date;
  const selectedDate = previewDate ? newTZDate(previewDate) : departDate;
  const selectedMotivators = useMemo(() => (selectedDate
    ? getSelectedMotivators([selectedDate]).filter(
      (item) => item !== BOOKING_MOTIVATOR_STATES.INSTABOOK,
    ) : []), [selectedDate, getSelectedMotivators]);

  const daysUntilInstabook = selectedDate
    ? differenceInDays(selectedDate, startOfToday())
    : undefined;

  if (formStep !== 'details') {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.left}>
        <div className={styles.dateAndDuration}>
          <DatePickerAndModal
            selectedMotivators={selectedMotivators}
            daysUntilInstabook={daysUntilInstabook}
          />
          <DurationField />
        </div>
        <div className={styles.departAndGuests}>
          <DepartureTimeField />
          <GuestField />
        </div>
        <div className={styles.captainOption}>
          <CaptainOption />
        </div>
        <div className={styles.divider}>
          <Divider />
        </div>
        <Typography type="span" size="body-l" weight="semibold">
          {tCommon('owner-message')}
        </Typography>
        <div className={styles.occassion}>
          <OccasionField
            name={OCCASION}
            fieldLevelValidation={false}
            occasions={occasions}
          />
        </div>
        <TextArea
          name={MESSAGE_TO_OWNER}
          label={tCommon('owner-message--label')}
          placeholder={tDirect('owner-message--placeholder')}
          showErrorBelow={false}
          toolTipContent={(
            <Trans
              t={tCommon}
              i18nKey="communications-warning--description"
              components={[
                <UniversalLink
                  className={styles.tooltipLink}
                  href={PATHS.TERMS}
                  openInNewTab
                />,
                <UniversalLink
                  className={styles.tooltipLink}
                  href={SUPPORT_URLS.WHY_COMMUNICATE_AND_PAY_THROUGH_GETMYBOAT}
                  openInNewTab
                />,
              ]}
            />
          )}
          showErrorOnlyIfTouched
        />
      </div>
      <div className={styles.right}>
        <div className={styles.pricing}>
          <PricingPanel />
        </div>
        {selectedMotivators && selectedMotivators.length > 0 && (
          <BookingMotivator
            motivators={selectedMotivators}
            daysUntilInstabook={daysUntilInstabook}
            blockedTimes={blockedTimes}
            blockedTimesLoading={blockedTimesLoading}
            visibleBlockedTimeCount={blockedTimes.length}
            showBlockTimeDescription={false}
          />
        )}
        <div className={styles.listing}>
          <Typography type="span" size="body-l" weight="semibold">
            {tDirect('listing')}
          </Typography>
          <div className={styles.img}>
            <Img
              src={listing.photo_url}
              alt={listing.headline}
              width={351}
              height={234}
              withPlaceholder
            />
          </div>
          <Typography type="p" size="body-m">
            {listing.headline}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default FormStepDetails;
